import { EligibilityAnswers } from '@common/neo.tax';
import { Stack, Typography } from '@ui-components/neo.tax';
import { ReactElement, useMemo } from 'react';

import { StyledCardStack, StyledContainerStack, StyledNoteCard } from './Confirmation.styled';
import { LoadingButton } from './LoadingButton';
import { Questions } from './rd-project-eligibility-review.utils';

interface ConfirmationProps {
  isLoading: false | 'BACK' | 'SAVE';
  onBack: () => void;
  onSave: () => void;
  currentAnswers: EligibilityAnswers;
  // The project will qualify but has no Technical Challenges / Process of Experimentation to inherit
  // from its tickets, so its R&D Details will stay Incomplete until a human fills them in.
  showIncompleteNarrativeNote: boolean;
}

export const Confirmation = ({
  currentAnswers,
  isLoading,
  onBack,
  onSave,
  showIncompleteNarrativeNote,
}: ConfirmationProps): ReactElement => {
  const isLoadingBack = useMemo(() => isLoading === 'BACK', [isLoading]);
  const isLoadingSave = useMemo(() => isLoading === 'SAVE', [isLoading]);
  const isDisabled = useMemo(() => isLoading !== false, [isLoading]);

  const getAnswer = (answer: boolean | null): string =>
    answer === true ? 'Yes' : answer === false ? 'No' : 'Not answered';

  // Format answers for display
  const formattedAnswers = useMemo(() => {
    const answers = [];
    if (currentAnswers.isTechnicallyUncertain !== null) {
      answers.push({
        question: Questions[0].shortText,
        answer: getAnswer(currentAnswers.isTechnicallyUncertain),
      });
    }
    if (currentAnswers.isCustomerFacingDevelopment !== null) {
      answers.push({
        question: Questions[1].shortText,
        answer: getAnswer(currentAnswers.isCustomerFacingDevelopment),
      });
    }
    if (currentAnswers.isInnovativeAndRisky !== null) {
      answers.push({
        question: Questions[2].shortText,
        answer: getAnswer(currentAnswers.isInnovativeAndRisky),
      });
    }
    return answers;
  }, [currentAnswers]);

  return (
    <StyledContainerStack direction="column" gap={4} paddingX={4} paddingY={3}>
      <Stack direction="column" gap={2} alignItems="start">
        <Stack direction="row" gap={2} alignItems="center">
          <Typography fontSize="sm" fontWeight="medium">
            Confirm Answers
          </Typography>
        </Stack>

        <Stack direction="column" gap={2}>
          {formattedAnswers.map((item, index) => (
            <StyledCardStack
              key={index}
              direction="column"
              gap={1}
              paddingX={3}
              paddingY={2}
              style={{ backgroundColor: 'white', borderRadius: '6px' }}
            >
              <Typography fontSize="xs" fontWeight="normal">
                {item.question}
              </Typography>
              <Typography fontSize="xs" fontWeight="medium">
                Answer: {item.answer}
              </Typography>
            </StyledCardStack>
          ))}
        </Stack>

        {showIncompleteNarrativeNote && (
          <StyledNoteCard direction="column" gap={1} paddingX={3} paddingY={2}>
            <Typography fontSize="xs" fontWeight="medium">
              This project will qualify, but its R&D details are incomplete.
            </Typography>
            <Typography fontSize="xs" fontWeight="normal">
              We couldn&apos;t find enough detail in its tickets to document the technical uncertainty and process of
              experimentation, so they&apos;ll stay blank and R&D Details will remain Incomplete. Edit them directly on
              the project to complete it — attaching a supporting document can also draft them, but re-runs the
              determination.
            </Typography>
          </StyledNoteCard>
        )}

        <Stack direction="column" gap={2} paddingY={0}>
          <Typography fontSize="sm" fontWeight="medium">
            Are you sure you want to confirm these answers?
          </Typography>
        </Stack>
      </Stack>

      {/* Display navigation buttons */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <LoadingButton variant="white" onClick={() => onBack()} isLoading={isLoadingBack} isDisabled={isDisabled}>
          Back
        </LoadingButton>
        <LoadingButton variant="primary" onClick={() => onSave()} isLoading={isLoadingSave} isDisabled={isDisabled}>
          Confirm
        </LoadingButton>
      </Stack>
    </StyledContainerStack>
  );
};
